/**
 * Pure: categorizes a Repair-Order-with-installer drop and decides whether
 * the #154 confirmation modal should fire.
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS:
 * --------------------------------------------------------------------------
 *
 * Same #73/#80/#83/#84/#85 pattern as the other drag-drop helpers
 * (`buildDragNDropUpdateFields`, `computeJobInitValues`). The categorization
 * decides which UI path runs — modal vs. silent — so it must be unit-
 * testable without spinning up React.
 *
 * --------------------------------------------------------------------------
 * RETURN SHAPE:
 * --------------------------------------------------------------------------
 *
 * Returns one of:
 *
 *   { shouldPrompt: false }
 *     Drop should NOT trigger the #154 confirm modal. Reasons:
 *       - Suppressed-row drop (handled by SuppressModal, separate concern)
 *       - Same-installer cell drop (installer is not changing)
 *       - Unrecognized drop ID format (defensive)
 *
 *   { shouldPrompt: true,
 *     dropType: "cell" | "unassigned-with-date" | "rts",
 *     dropTargetInstaller: string }
 *     Drop SHOULD trigger the modal. The caller passes these fields into
 *     openRepairOrderConfirmModal so the modal renders the right copy + buttons.
 *
 * --------------------------------------------------------------------------
 * INPUT CONTRACT:
 * --------------------------------------------------------------------------
 *
 * @param {Object} params
 * @param {{id: string}} params.dropObject - The drop target descriptor.
 * @param {Object} params.foundDocument - The Repair Order being dragged.
 *   Must already have passed `isRepairOrderWithInstaller(foundDocument)`
 *   before this call — this helper assumes the caller has gated on that.
 * @returns {{shouldPrompt: boolean, dropType?: string, dropTargetInstaller?: string}}
 */
export function categorizeRepairOrderDrop({ dropObject, foundDocument }) {
  const dropId = dropObject?.id || "";

  // RTS drop — no date, no installer in drop ID.
  if (dropId === "Unassigned_RTS") {
    return { shouldPrompt: true, dropType: "rts", dropTargetInstaller: "" };
  }

  // Unassigned-with-date drop — Unassigned_<DayName>(_date).
  if (dropId.slice(0, 10) === "Unassigned") {
    return {
      shouldPrompt: true,
      dropType: "unassigned-with-date",
      dropTargetInstaller: "",
    };
  }

  // Crew-cell drop — <payrollNo (7 chars)>_<columnIdx>(_date).
  // Payroll# is the first 7 characters of the drop ID.
  if (dropId.length >= 9 && dropId[7] === "_") {
    const dropTargetInstaller = dropId.slice(0, 7);
    const originalInstaller = String(foundDocument?.installer ?? "").trim();
    if (dropTargetInstaller === originalInstaller) {
      // Same installer — installer not changing → no prompt.
      return { shouldPrompt: false };
    }
    return {
      shouldPrompt: true,
      dropType: "cell",
      dropTargetInstaller,
    };
  }

  // Unrecognized drop format — defensive.
  return { shouldPrompt: false };
}
